<script setup lang="ts">
import { computed, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useIndmoRichTextIcons } from '@/composables/useIndmoRichTextIcons';
import { useAuthStore } from '@/stores/auth';
import {
  authSnapshotFromSession,
  authorisationRequiredCtaTarget,
  type AuthorisationRequiredAuthMode,
} from '@/components/contentBlocks';

const props = defineProps<{
  /** Expanded HTML from Wagtail rich text (pages API). */
  body?: string;
  /** Same HTML from Wagtail block-picker preview query (`html`). */
  html?: string;
  /** Present in API payload; visibility is resolved before render. */
  required_auth?: AuthorisationRequiredAuthMode;
}>();

const { t } = useI18n();
const authStore = useAuthStore();

const richHtml = computed(() => props.html ?? props.body ?? '');

const authSnapshot = computed(() =>
  authSnapshotFromSession(authStore.isReady, authStore.isAuthenticated, authStore.currentUser)
);

const ctaTarget = computed(() => authorisationRequiredCtaTarget(authSnapshot.value));

const ctaTo = computed(() => {
  switch (ctaTarget.value) {
    case 'register':
      return { name: 'account-signup' as const };
    case 'submissions':
      return { name: 'account-tab' as const, params: { section: 'submissions' as const } };
    default:
      return { name: 'account' as const };
  }
});

const ctaLabel = computed(() => {
  switch (ctaTarget.value) {
    case 'register':
      return t('blocks.authorisationRequired.createAccount');
    case 'submissions':
      return t('blocks.authorisationRequired.goToSubmissions');
    default:
      return t('blocks.authorisationRequired.goToAccount');
  }
});

const rootEl = ref<HTMLElement | null>(null);
useIndmoRichTextIcons(rootEl, () => richHtml.value);
</script>

<template>
  <section class="block-auth-required" :data-required-auth="required_auth ?? 'account'">
    <div ref="rootEl" class="block-auth-required__body" v-html="richHtml" />
    <RouterLink class="block-auth-required__cta" :to="ctaTo">
      {{ ctaLabel }}
    </RouterLink>
  </section>
</template>

<style scoped>
.block-auth-required {
  margin: 1.5rem 0;
  padding: 1.25rem;
  border: 1px solid var(--color-border, #ccc);
  border-radius: 6px;
  background-color: var(--color-surface-muted, #f7f7f7);
}

.block-auth-required__body {
  margin: 0 0 1rem;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  line-height: 1.625;
}

@media (min-width: 640px) {
  .block-auth-required__body {
    font-size: 1rem;
  }
}

.block-auth-required__body :deep(p:last-child) {
  margin-block-end: 0;
}

.block-auth-required__cta {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.9375rem;
  text-decoration: none;
  color: var(--color-on-primary, #fff);
  background-color: var(--color-primary, #1a5f4a);
}

.block-auth-required__cta:hover {
  opacity: 0.92;
}
</style>
